import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {ZadProcurementDocument} from '../../../models/zad'
import {ZadApiError} from '../zad/errors'
import {
  createZadProcurementDocument,
  convertZadProcurementToPurchase,
  fetchZadProcurementById,
  fetchZadProcurementDocuments,
  fetchZadProcurementPickerProducts,
  fetchZadProcurementPickerSuppliers,
  fetchZadProcurementPrint,
  removeZadProcurementAttachment,
  submitZadProcurementQuote,
  updateZadProcurementDocument,
  updateZadProcurementStatus,
  uploadZadProcurementAttachment,
} from './service'
import {FetchZadProcurementListResponse, ZadProcurementPrefill, ZadProcurementState} from './types'

const initialState: ZadProcurementState = {
  items: [],
  total: 0,
  totalPages: 0,
  listLoading: false,
  listInitialized: false,
  actionLoading: false,
  selected: null,
  print: null,
  printIssuer: null,
  pickerSuppliers: [],
  pickerProducts: [],
  pickerLoading: false,
  error: null,
  errorCode: null,
  errorStatus: null,
  prefill: null,
}

function applyRejected(state: ZadProcurementState, action: PayloadAction<any>) {
  const payload = (action.payload || {}) as ZadApiError
  state.error = payload.error || 'Request failed'
  state.errorCode = payload.errorCode || null
  state.errorStatus = payload.status || null
}

function clearError(state: ZadProcurementState) {
  state.error = null
  state.errorCode = null
  state.errorStatus = null
}

function upsertDocument(state: ZadProcurementState, document?: ZadProcurementDocument | null) {
  if (!document?._id) return
  state.selected = document
  const index = state.items.findIndex((item) => item._id === document._id)
  if (index >= 0) state.items[index] = document
  else state.items = [document, ...state.items]
}

const zadProcurementSlice = createSlice({
  name: 'zadProcurement',
  initialState,
  reducers: {
    setZadProcurementPrefill: (state: ZadProcurementState, action: PayloadAction<ZadProcurementPrefill>) => {
      state.prefill = action.payload
    },
    clearZadProcurementPrefill: (state: ZadProcurementState) => {
      state.prefill = null
    },
    clearSelectedZadProcurement: (state: ZadProcurementState) => {
      state.selected = null
      state.print = null
      state.printIssuer = null
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchZadProcurementDocuments.pending, (state: ZadProcurementState) => {
      state.listLoading = true
      clearError(state)
    })
    builder.addCase(
      fetchZadProcurementDocuments.rejected,
      (state: ZadProcurementState, action: PayloadAction<any>) => {
        applyRejected(state, action)
        state.listLoading = false
        state.listInitialized = true
      }
    )
    builder.addCase(
      fetchZadProcurementDocuments.fulfilled,
      (state: ZadProcurementState, action: PayloadAction<FetchZadProcurementListResponse>) => {
        state.listLoading = false
        state.listInitialized = true
        clearError(state)
        state.items = action.payload.items
        state.total = action.payload.total
        state.totalPages = action.payload.totalPages
      }
    )

    const pendingAction = (state: ZadProcurementState) => {
      state.actionLoading = true
    }
    const rejectAction = (state: ZadProcurementState, action: PayloadAction<any>) => {
      state.actionLoading = false
      applyRejected(state, action)
    }
    const fulfillDocument = (state: ZadProcurementState, action: PayloadAction<any>) => {
      state.actionLoading = false
      clearError(state)
      upsertDocument(state, action.payload.document)
    }

    builder.addCase(fetchZadProcurementById.pending, pendingAction)
    builder.addCase(fetchZadProcurementById.rejected, rejectAction)
    builder.addCase(fetchZadProcurementById.fulfilled, fulfillDocument)
    builder.addCase(createZadProcurementDocument.pending, pendingAction)
    builder.addCase(createZadProcurementDocument.rejected, rejectAction)
    builder.addCase(createZadProcurementDocument.fulfilled, fulfillDocument)
    builder.addCase(updateZadProcurementDocument.pending, pendingAction)
    builder.addCase(updateZadProcurementDocument.rejected, rejectAction)
    builder.addCase(updateZadProcurementDocument.fulfilled, fulfillDocument)
    builder.addCase(updateZadProcurementStatus.pending, pendingAction)
    builder.addCase(updateZadProcurementStatus.rejected, rejectAction)
    builder.addCase(updateZadProcurementStatus.fulfilled, fulfillDocument)
    builder.addCase(submitZadProcurementQuote.pending, pendingAction)
    builder.addCase(submitZadProcurementQuote.rejected, rejectAction)
    builder.addCase(submitZadProcurementQuote.fulfilled, fulfillDocument)
    builder.addCase(uploadZadProcurementAttachment.pending, pendingAction)
    builder.addCase(uploadZadProcurementAttachment.rejected, rejectAction)
    builder.addCase(uploadZadProcurementAttachment.fulfilled, fulfillDocument)
    builder.addCase(removeZadProcurementAttachment.pending, pendingAction)
    builder.addCase(removeZadProcurementAttachment.rejected, rejectAction)
    builder.addCase(removeZadProcurementAttachment.fulfilled, fulfillDocument)

    builder.addCase(convertZadProcurementToPurchase.pending, pendingAction)
    builder.addCase(convertZadProcurementToPurchase.rejected, rejectAction)
    builder.addCase(convertZadProcurementToPurchase.fulfilled, (state: ZadProcurementState, action) => {
      state.actionLoading = false
      clearError(state)
      if (action.payload.document) {
        upsertDocument(state, action.payload.document)
        return
      }
      if (state.selected && action.payload.conversion) {
        state.selected = {...state.selected, conversion: action.payload.conversion}
      }
    })

    builder.addCase(fetchZadProcurementPrint.fulfilled, (state: ZadProcurementState, action) => {
      state.print = action.payload.document
      state.printIssuer = action.payload.issuer || null
    })

    builder.addCase(fetchZadProcurementPickerSuppliers.pending, (state: ZadProcurementState) => {
      state.pickerLoading = true
    })
    builder.addCase(fetchZadProcurementPickerSuppliers.rejected, (state: ZadProcurementState) => {
      state.pickerLoading = false
    })
    builder.addCase(fetchZadProcurementPickerSuppliers.fulfilled, (state: ZadProcurementState, action) => {
      state.pickerLoading = false
      state.pickerSuppliers = action.payload.suppliers
    })
    builder.addCase(fetchZadProcurementPickerProducts.pending, (state: ZadProcurementState) => {
      state.pickerLoading = true
    })
    builder.addCase(fetchZadProcurementPickerProducts.rejected, (state: ZadProcurementState) => {
      state.pickerLoading = false
    })
    builder.addCase(fetchZadProcurementPickerProducts.fulfilled, (state: ZadProcurementState, action) => {
      state.pickerLoading = false
      state.pickerProducts = action.payload.products
    })
  },
})

export default zadProcurementSlice.reducer
export const actions = zadProcurementSlice.actions
export {initialState as zadProcurementInitialState}
